/**
 * Maps a `CircuitIR` to the ELK graph shape consumed by `elkjs`.
 *
 * Per IR plan §7.2, ELK's `layered` algorithm with port constraints
 * (`portConstraints: FIXED_ORDER`, per-port `side`) is the right shape
 * for IC-heavy schematics. This module derives port `side` from each
 * pin's `function`:
 *
 *   * PWRIN  → NORTH (power flows down from the top rail)
 *   * PWROUT → SOUTH (regulated power exits at the bottom)
 *   * INPUT  → WEST  (signal flow left-to-right)
 *   * OUTPUT → EAST
 *   * PASSIVE / BIDI → nearest free side, default WEST
 *
 * EE-convention enforcement (§7.4 Option A) rides on the ELK options
 * — `direction: RIGHT`, `layeringStrategy: NETWORK_SIMPLEX`, fixed
 * power/ground positions are layered in by `eeConventions.ts` (TBD).
 *
 * The mapper is pure / side-effect-free so it's testable without the
 * ELK runtime.
 */
import type { CircuitIR, Component, PinFunction } from "@models/circuit-ir";

/** Subset of the ELK input graph type we actually emit. Mirrors the
 *  `ElkNode` / `ElkPort` / `ElkEdge` shapes from `elkjs/lib/elk-api`. */
export interface ElkInputNode {
  id: string;
  width: number;
  height: number;
  labels?: { text: string }[];
  ports?: ElkInputPort[];
  layoutOptions?: Record<string, string>;
}

export interface ElkInputPort {
  id: string;
  width: number;
  height: number;
  labels?: { text: string }[];
  layoutOptions?: Record<string, string>;
}

export interface ElkInputEdge {
  id: string;
  sources: string[];
  targets: string[];
}

export interface ElkInputGraph {
  id: "root";
  layoutOptions: Record<string, string>;
  children: ElkInputNode[];
  edges: ElkInputEdge[];
}

export const PIN_SIDE: Record<
  PinFunction,
  "NORTH" | "SOUTH" | "EAST" | "WEST"
> = {
  pwrin: "NORTH",
  pwrout: "SOUTH",
  input: "WEST",
  output: "EAST",
  bidi: "EAST",
  passive: "WEST",
};

/**
 * Estimate node dimensions from pin count + the longest label —
 * tighter than ELK's default sizing because schematic symbols are
 * roughly square per port. Picked empirically against the `elkjs`
 * react-flow example.
 */
function nodeSize(component: Component): { width: number; height: number } {
  const pinCount = component.pins.length || 1;
  const longestLabel = Math.max(
    component.refdes.length,
    (component.value || "").length,
    ...component.pins.map((p) => p.number.length + (p.name?.length ?? 0)),
  );
  const labelWidth = Math.max(80, longestLabel * 7 + 32);
  const sideWidth = Math.max(48, pinCount * 12 + 24);
  // For ICs and similar multi-pin parts, scale height with pin count;
  // 2-terminal passives are squat.
  const tall = pinCount > 4;
  return {
    width: tall ? Math.max(120, sideWidth) : labelWidth,
    height: tall ? Math.max(80, pinCount * 18) : 60,
  };
}

export interface IrToElkOptions {
  /** ELK direction. Default `RIGHT` matches schematic L-to-R signal flow. */
  direction?: "RIGHT" | "DOWN" | "LEFT" | "UP";
  /** Whether to include unconnected components. Default true. */
  includeOrphans?: boolean;
}

/**
 * Map a CircuitIR to an ELK input graph. Pure; no DOM access; safe to
 * call from the Web Worker.
 *
 * One ELK node per Component, one ELK port per Pin, one ELK edge per
 * NetEndpoint pair. We expand a net into a star around its first
 * endpoint — ELK's edge model is point-to-point, so a 3-endpoint net
 * becomes 2 edges. Visually fine for chat-bubble previews.
 */
export function irToElkGraph(
  ir: CircuitIR,
  opts: IrToElkOptions = {},
): ElkInputGraph {
  const { direction = "RIGHT", includeOrphans = true } = opts;

  const referencedComponentIds = new Set<string>();
  for (const net of ir.nets) {
    for (const ep of net.endpoints) {
      referencedComponentIds.add(ep.component_id);
    }
  }

  const visibleComponents = includeOrphans
    ? ir.components
    : ir.components.filter((c) => referencedComponentIds.has(c.id));

  const children: ElkInputNode[] = visibleComponents.map((comp) => {
    const { width, height } = nodeSize(comp);
    return {
      id: comp.id,
      width,
      height,
      labels: [{ text: comp.refdes || comp.id.slice(0, 6) }],
      ports: comp.pins.map((pin) => ({
        id: portId(comp.id, pin.number),
        width: 8,
        height: 8,
        labels: [{ text: pin.number }],
        layoutOptions: {
          "elk.port.side": PIN_SIDE[pin.function] ?? "WEST",
        },
      })),
      layoutOptions: {
        // Force per-node port-order honouring; without this ELK is free
        // to permute pins on a side, which scrambles IC pinouts.
        "elk.portConstraints": "FIXED_ORDER",
      },
    };
  });

  const edges: ElkInputEdge[] = [];
  let edgeCounter = 0;
  for (const net of ir.nets) {
    if (net.endpoints.length < 2) continue;
    const [first, ...rest] = net.endpoints;
    for (const ep of rest) {
      edges.push({
        id: `e-${net.id}-${edgeCounter++}`,
        sources: [portId(first.component_id, first.pin_number)],
        targets: [portId(ep.component_id, ep.pin_number)],
      });
    }
  }

  return {
    id: "root",
    layoutOptions: {
      "elk.algorithm": "layered",
      "elk.direction": direction,
      // EE conventions Option A — bake into ELK options before any LLM
      // post-pass (§7.4). Fixed positions for power / ground would be
      // layered in by `eeConventions.ts` once the IR consistently flags
      // is_power / is_ground.
      "elk.layered.spacing.nodeNodeBetweenLayers": "60",
      "elk.spacing.nodeNode": "40",
      "elk.spacing.portPort": "16",
      "elk.layered.nodePlacement.strategy": "NETWORK_SIMPLEX",
      "elk.edgeRouting": "ORTHOGONAL",
    },
    children,
    edges,
  };
}

/** `portId` is the canonical encoding ELK uses to address a (component, pin)
 *  pair as a single string. Round-trip only — never persisted. */
export function portId(componentId: string, pinNumber: string): string {
  return `${componentId}::${pinNumber}`;
}

export function parsePortId(pid: string): {
  componentId: string;
  pinNumber: string;
} {
  const idx = pid.indexOf("::");
  return idx === -1
    ? { componentId: pid, pinNumber: "" }
    : {
        componentId: pid.slice(0, idx),
        pinNumber: pid.slice(idx + 2),
      };
}
